import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { activatePreview, sendActivateCode, activate } from '../../api';
import { getApiErrorMessage } from '../../utils/apiErrorUtils';
import './index.css';
import './index.tablet.css';
import './index.mobile.css';

// Step indicator icons from Figma
const ICON_STEP_ACTIVE_OUTER = `${process.env.PUBLIC_URL}/image/Ellipse_130.svg`;
const ICON_STEP_ACTIVE_INNER = `${process.env.PUBLIC_URL}/image/Ellipse_129.svg`;
const ICON_STEP_INACTIVE = `${process.env.PUBLIC_URL}/image/Ellipse_129_1.svg`;
const ICON_STEP_DONE = `${process.env.PUBLIC_URL}/image/Frame_2608279.svg`;
const ICON_SUCCESS = `${process.env.PUBLIC_URL}/image/Frame_2608279_1.svg`;

const RESEND_SECONDS = 60;
const REDIRECT_SECONDS = 5;
const OTP_LENGTH = 6;

function StepIndicator({ currentStep, t }) {
  const steps = [
    { key: 'step1', label: t('activate.step1') },
    { key: 'step2', label: t('activate.step2') },
    { key: 'step3', label: t('activate.step3') },
  ];

  return (
    <div className="act-steps">
      {steps.map((step, index) => {
        const stepNum = index + 1;
        const isDone = stepNum < currentStep;
        const isActive = stepNum === currentStep;

        return (
          <React.Fragment key={step.key}>
            <div className="act-step-item">
              <div className="act-step-icon">
                {isDone ? (
                  <img src={ICON_STEP_DONE} alt="" className="act-step-done-icon" />
                ) : isActive ? (
                  <>
                    <img src={ICON_STEP_ACTIVE_OUTER} alt="" className="act-step-active-outer" />
                    <img src={ICON_STEP_ACTIVE_INNER} alt="" className="act-step-active-inner" />
                    <span className="act-step-num act-step-num--active">{stepNum}</span>
                  </>
                ) : (
                  <>
                    <img src={ICON_STEP_INACTIVE} alt="" className="act-step-inactive-inner" />
                    <span className="act-step-num act-step-num--inactive">{stepNum}</span>
                  </>
                )}
              </div>
              <span
                className={`act-step-label ${isActive || isDone ? 'act-step-label--active' : 'act-step-label--inactive'}`}
              >
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`act-step-line ${isDone || (isActive && index === 0 && currentStep >= 2) ? 'act-step-line--done' : ''} ${currentStep === 3 && index === 1 ? 'act-step-line--done' : ''}`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function Step1({ t, companyData, onSendCode }) {
  return (
    <div className="act-step1">
      <h2 className="act-step1-welcome">{t('activate.step1.welcome')}</h2>
      <p className="act-step1-subtitle">{t('activate.step1.subtitle')}</p>

      <div className="act-info-card">
        <div className="act-info-row">
          <span className="act-info-label">{t('activate.step1.companyName')}</span>
          <span className="act-info-value">{companyData.companyName}</span>
        </div>
        <div className="act-info-row">
          <span className="act-info-label">{t('activate.step1.companyAccount')}</span>
          <span className="act-info-value act-info-value--account">{companyData.accountId}</span>
        </div>
        <div className="act-info-row">
          <span className="act-info-label">{t('activate.step1.email')}</span>
          <span className="act-info-value">{companyData.maskedEmail}</span>
        </div>
      </div>

      <button className="act-btn-primary" onClick={onSendCode}>
        {t('activate.step1.sendCode')}
      </button>
    </div>
  );
}

function OtpInput({ value, onChange }) {
  const inputs = useRef([]);

  const handleChange = (index, e) => {
    const char = e.target.value.replace(/\D/g, '').slice(-1);
    const next = value.split('');
    next[index] = char;
    const newVal = next.join('');
    onChange(newVal);
    if (char && index < OTP_LENGTH - 1) {
      inputs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (!value[index] && index > 0) {
        inputs.current[index - 1]?.focus();
        const next = value.split('');
        next[index - 1] = '';
        onChange(next.join(''));
      } else {
        const next = value.split('');
        next[index] = '';
        onChange(next.join(''));
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    onChange(pasted.padEnd(OTP_LENGTH, '').slice(0, OTP_LENGTH));
    const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
    inputs.current[focusIdx]?.focus();
  };

  return (
    <div className="act-otp-row">
      {Array.from({ length: OTP_LENGTH }, (_, i) => (
        <input
          key={i}
          ref={(el) => (inputs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[i] || ''}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          className={`act-otp-box ${value[i] ? 'act-otp-box--filled' : ''}`}
        />
      ))}
    </div>
  );
}

function getPasswordStrength(password) {
  if (!password) return 0;
  let score = 0;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  return score;
}

function PasswordStrengthBar({ password }) {
  const strength = getPasswordStrength(password);
  // 1 bar = weak (red), 2 bars = medium (orange), 3 bars = strong (green)
  const levelColor = strength >= 3 ? '#069c67' : strength === 2 ? '#ff7700' : '#f54336';

  return (
    <div className="act-strength-wrap">
      <div className="act-strength-bars">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="act-strength-bar"
            style={{ background: strength > i ? levelColor : '#d9d9d9' }}
          />
        ))}
      </div>
    </div>
  );
}

function Step2({ t, maskedEmail, enterpriseId, locale, onSubmit, onBack }) {
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [countdown, setCountdown] = useState(0);
  const countdownRef = useRef(null);
  const [errors, setErrors] = useState({});

  const startCountdown = useCallback(() => {
    setCountdown(RESEND_SECONDS);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    startCountdown();
    return () => clearInterval(countdownRef.current);
  }, [startCountdown]);

  const handleResend = async () => {
    if (countdown > 0) return;
    try {
      await sendActivateCode(enterpriseId, locale);
    } catch {}
    startCountdown();
  };

  const validate = () => {
    const errs = {};
    if (otp.replace(/\D/g, '').length < OTP_LENGTH) errs.otp = true;
    if (password.length < 8 || password.length > 20) errs.password = true;
    if (getPasswordStrength(password) < 3) errs.passwordComplexity = true;
    if (password !== confirmPassword) errs.confirmPassword = true;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) onSubmit({ otp, password });
  };

  return (
    <div className="act-step2">
      <div className="act-code-sent-banner">
        <span className="act-code-sent-text">
          {t('activate.step2.codeSentTo')} <strong>{maskedEmail}</strong>
        </span>
      </div>

      <div className="act-field">
        <div className="act-field-label">
          <span className="act-required">*</span>
          {t('activate.step2.codeLabel')}
        </div>
        <OtpInput value={otp} onChange={setOtp} />
        <div className="act-otp-footer">
          <span className="act-otp-valid">{t('activate.step2.codeValid')}</span>
          <button
            className={`act-resend-btn ${countdown > 0 ? 'act-resend-btn--disabled' : ''}`}
            onClick={handleResend}
            disabled={countdown > 0}
          >
            {countdown > 0
              ? t('activate.step2.resendCountdown', { seconds: countdown })
              : t('activate.step2.resendCode')}
          </button>
        </div>
        <span className="act-spam-hint">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M8.00067 0.666748C12.0505 0.6671 15.3337 3.95086 15.3337 8.00073C15.3333 12.0503 12.0502 15.3334 8.00067 15.3337C3.9508 15.3337 0.667039 12.0505 0.666687 8.00073C0.666687 3.95064 3.95058 0.666748 8.00067 0.666748ZM8.00067 2.00073C4.68696 2.00073 2.00067 4.68702 2.00067 8.00073C2.00102 11.3141 4.68718 14.0007 8.00067 14.0007C11.3139 14.0004 14.0003 11.3139 14.0007 8.00073C14.0007 4.68724 11.3141 2.00108 8.00067 2.00073ZM8.66669 10.6667H9.66669V11.9998H6.33368V10.6667H7.33368V7.33374H6.66669V5.99976H8.66669V10.6667ZM8.00067 3.66675C8.4605 3.6671 8.8335 4.03989 8.83368 4.49976C8.83368 4.95978 8.46061 5.33339 8.00067 5.33374C7.54043 5.33374 7.16669 4.95999 7.16669 4.49976C7.16686 4.03967 7.54054 3.66675 8.00067 3.66675Z"
              fill="#AAAAAA"
            />
          </svg>
          {t('activate.step2.spamHint')}
        </span>
      </div>

      <div className="act-field">
        <div className="act-field-label">
          <span className="act-required">*</span>
          {t('activate.step2.passwordLabel')}
        </div>
        <input
          type="password"
          className={`act-input ${errors.password || errors.passwordComplexity ? 'act-input--error' : ''}`}
          placeholder={t('activate.step2.passwordPlaceholder')}
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (errors.password || errors.passwordComplexity) {
              setErrors((prev) => ({ ...prev, password: false, passwordComplexity: false }));
            }
          }}
        />
        <PasswordStrengthBar password={password} />
        <p className={`act-hint`}>{t('activate.step2.passwordHint')}</p>
      </div>

      <div className="act-field">
        <div className="act-field-label">
          <span className="act-required">*</span>
          {t('activate.step2.confirmPasswordLabel')}
        </div>
        <input
          type="password"
          className={`act-input ${errors.confirmPassword ? 'act-input--error' : ''}`}
          placeholder={t('activate.step2.confirmPasswordPlaceholder')}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
      </div>

      <button className="act-btn-primary" onClick={handleSubmit}>
        {t('activate.step2.submit')}
      </button>

      <button className="act-back-btn" onClick={onBack}>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10 12L6 8L10 4"
            stroke="#0F74EF"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {t('activate.step2.back')}
      </button>
    </div>
  );
}

function Step3({ t, onEnter }) {
  const [seconds, setSeconds] = useState(REDIRECT_SECONDS);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setInterval(() => {
      setSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/login');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [navigate]);

  return (
    <div className="act-step3">
      <div className="act-success-icon-wrap">
        <img src={ICON_SUCCESS} alt="success" className="act-success-icon" />
      </div>
      <h2 className="act-success-title">{t('activate.step3.success')}</h2>
      <p className="act-success-subtitle">{t('activate.step3.redirecting', { seconds })}</p>
      <button className="act-btn-primary" onClick={onEnter}>
        {t('activate.step3.enterPortal')}
      </button>
    </div>
  );
}

function Activate() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cloudBgUrl = `${process.env.PUBLIC_URL}/image/云朵背景.jpg`;

  const enterpriseId = searchParams.get('id');
  const [step, setStep] = useState(1);
  const [companyData, setCompanyData] = useState({
    companyName: '',
    accountId: '',
    maskedEmail: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!enterpriseId) {
      setError(t('activate.error.noId'));
      setLoading(false);
      return;
    }
    activatePreview(enterpriseId)
      .then((res) => {
        const r = res.result;
        setCompanyData({
          companyName: r.companyName,
          accountId: r.userid,
          maskedEmail: r.loginEmailMasked,
        });
      })
      .catch((err) => setError(getApiErrorMessage(err?.message, t, 'activate.error.invalid')))
      .finally(() => setLoading(false));
  }, [enterpriseId, t]);

  const handleSendCode = async () => {
    try {
      await sendActivateCode(enterpriseId, i18n.language);
      setStep(2);
    } catch (err) {
      setError(getApiErrorMessage(err.message, t, 'activate.error.sendCode'));
    }
  };

  const handleSetPassword = async ({ otp, password }) => {
    try {
      await activate(enterpriseId, otp, password);
      setStep(3);
    } catch (err) {
      setError(getApiErrorMessage(err.message, t, 'activate.error.activate'));
    }
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleEnterPortal = () => {
    navigate('/login');
  };

  if (loading) return <div className="act-page" />;

  return (
    <div className="act-page">
      <div className="act-cloud-bg" style={{ backgroundImage: `url(${cloudBgUrl})` }} />
      <div className="act-card">
        <h1 className="act-card-title">{t('activate.title')}</h1>

        {error ? (
          <p style={{ color: '#f54336', textAlign: 'center', padding: '24px 0' }}>{error}</p>
        ) : (
          <>
            <StepIndicator currentStep={step} t={t} />
            {step === 1 && <Step1 t={t} companyData={companyData} onSendCode={handleSendCode} />}
            {step === 2 && (
              <Step2
                t={t}
                maskedEmail={companyData.maskedEmail}
                enterpriseId={enterpriseId}
                locale={i18n.language}
                onSubmit={handleSetPassword}
                onBack={handleBack}
              />
            )}
            {step === 3 && <Step3 t={t} onEnter={handleEnterPortal} />}
          </>
        )}
      </div>
    </div>
  );
}

export default Activate;
